import React, { useState, useEffect, useMemo } from "react";
import { Form, Input, Select, Row, Col, Radio, message, Spin, Tooltip } from "antd";
import {
  UploadOutlined, FileOutlined, LoadingOutlined, RobotOutlined, CheckCircleFilled,
} from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Option } = Select;

const UploadZone = ({ label, required, hint, inlineHint, file, path, uploading, onSelect, onRemove, accept, minHeight }) => {
  const handleChange = (e) => {
    const f = e.target.files[0];
    if (f) onSelect(f);
    e.target.value = "";
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: "#334155", marginBottom: 6 }}>
        {label}{required && <span style={{ color: "#ff4d4f" }}> *</span>}
        {inlineHint && <span style={{ fontWeight: 400, fontSize: 12, color: "#94a3b8", marginLeft: 6 }}>({inlineHint})</span>}
      </div>
      {hint && <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 6 }}>{hint}</div>}

      <label style={{
        display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
        border: `1.5px dashed ${path ? "#01d9a9" : "#e2e8f0"}`,
        borderRadius: 8, cursor: uploading ? "not-allowed" : "pointer",
        background: path ? "#f0fdfb" : "#fafafa",
        transition: "all 0.2s", position: "relative",
        ...(minHeight ? { minHeight } : {}),
      }}>
        <input type="file" accept={accept || "*"} style={{ display: "none" }} onChange={handleChange} disabled={uploading} />
        {uploading
          ? <Spin indicator={<LoadingOutlined spin />} size="small" />
          : path
            ? <CheckCircleFilled style={{ color: "#01d9a9", fontSize: 16 }} />
            : <UploadOutlined style={{ color: "#94a3b8", fontSize: 16 }} />}
        <span style={{ fontSize: 13, color: path ? "#014c75" : "#94a3b8", fontWeight: path ? 600 : 400, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {uploading ? "Uploading..." : file ? file.name : path ? "Existing document on file" : `Click to upload ${label}`}
        </span>
        {path && !uploading && (
          <span onClick={(e) => { e.preventDefault(); onRemove(); }} style={{ fontSize: 12, color: "#ff4d4f", fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>Remove</span>
        )}
      </label>
    </div>
  );
};

const AvailabilityDocuments = ({ initialData, onValidate, resumeFile: prefillResumeFile, onPathsChange }) => {
  const [form] = Form.useForm();
  const [idProofType, setIdProofType] = useState(initialData.idProofType || "aadhaar");

  const sessionId = useMemo(() => `TEMP-${Date.now()}`, []);
  const odataId = initialData.id || sessionId;

  const [resumeFile, setResumeFile] = useState(null);
  const [resumePath, setResumePath] = useState(initialData.resumeUrl || null);
  const [resumeUploading, setResumeUploading] = useState(false);

  const [idProofFile, setIdProofFile] = useState(null);
  const [idProofPath, setIdProofPath] = useState(initialData.aadhaarUrl || initialData.passportUrl || null);
  const [idProofUploading, setIdProofUploading] = useState(false);

  const [panFile, setPanFile] = useState(null);
  const [panPath, setPanPath] = useState(initialData.panUrl || null);
  const [panUploading, setPanUploading] = useState(false);

  const [degreeFile, setDegreeFile] = useState(null);
  const [degreePath, setDegreePath] = useState(initialData.degreeUrl || null);
  const [degreeUploading, setDegreeUploading] = useState(false);

  const [extracting, setExtracting] = useState(null);

  useEffect(() => {
    form.setFieldsValue(initialData);
    if (prefillResumeFile && !resumePath) {
      handleResumeSelect(prefillResumeFile);
    }
  }, [initialData]);

  useEffect(() => {
    if (onPathsChange) {
      onPathsChange({
        resumeUrl: resumePath,
        aadhaarUrl: idProofType === "aadhaar" ? idProofPath : null,
        passportUrl: idProofType === "passport" ? idProofPath : null,
        panUrl: panPath,
        degreeUrl: degreePath,
        idProofType,
      });
    }
  }, [resumePath, idProofPath, panPath, degreePath, idProofType]);

  useEffect(() => {
    onValidate(async () => {
      const values = await form.validateFields();
      if (!resumePath) { message.error("Resume is required. Please upload a resume."); throw new Error("resume"); }
      if (!idProofPath) { message.error("ID proof is required (Aadhaar or Passport)."); throw new Error("idproof"); }
      if (!panPath) { message.error("PAN card is required."); throw new Error("pan"); }
      return {
        ...values,
        resumeUrl: resumePath,
        aadhaarUrl: idProofType === "aadhaar" ? idProofPath : null,
        passportUrl: idProofType === "passport" ? idProofPath : null,
        panUrl: panPath,
        degreeUrl: degreePath,
        idProofType,
      };
    });
  }, [form, onValidate, resumePath, idProofPath, panPath, degreePath, idProofType, initialData]);

  const getToken = () => localStorage.getItem("authToken");

  const uploadPresigned = async (file, docType) => {
    const token = getToken();
    const userName = initialData.name || "Unknown";
    const res = await axios.post(API_CONST.UPLOAD_REQUEST_URL, {
      name: file.name, userName, odataId, size: file.size, contentType: file.type, documentType: docType,
    }, { headers: { Authorization: `Bearer ${token}` } });
    const { uploadURL, objectPath } = res.data;
    await fetch(uploadURL, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
    return objectPath;
  };

  const uploadWithOCR = async (file, docType, ocrField) => {
    const token = getToken();
    const userName = initialData.name || "Unknown";
    const fd = new FormData();
    fd.append("file", file);
    fd.append("documentType", docType);
    fd.append("userName", userName);
    fd.append("odataId", odataId);
    const res = await axios.post(API_CONST.UPLOAD_DOCUMENT_WITH_OCR, fd, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
    });
    const { objectPath, extracted } = res.data;
    if (extracted?.idNumber && ocrField) {
      form.setFieldValue(ocrField, extracted.idNumber);
      message.success(`${docType.toUpperCase()} number auto-extracted`);
    }
    return objectPath;
  };

  const handleResumeSelect = async (file) => {
    setResumeFile(file);
    setResumeUploading(true);
    try {
      const p = await uploadPresigned(file, "resume");
      setResumePath(p);
    } catch (e) {
      message.error(`Resume upload failed: ${e.message}`);
      setResumeFile(null); setResumePath(null);
    } finally { setResumeUploading(false); }
  };

  const handleIdProofSelect = async (file) => {
    setIdProofFile(file); setIdProofUploading(true);
    try {
      const ocrField = idProofType === "aadhaar" ? "aadhaarNumber" : "passportNumber";
      const p = await uploadWithOCR(file, idProofType, ocrField);
      setIdProofPath(p);
    } catch (e) {
      message.error(`${idProofType} upload failed: ${e.message}`);
      setIdProofFile(null); setIdProofPath(null);
    } finally { setIdProofUploading(false); }
  };

  const handlePanSelect = async (file) => {
    setPanFile(file); setPanUploading(true);
    try {
      const p = await uploadWithOCR(file, "pan", "panNumber");
      setPanPath(p);
    } catch (e) {
      message.error(`PAN upload failed: ${e.message}`);
      setPanFile(null); setPanPath(null);
    } finally { setPanUploading(false); }
  };

  const handleDegreeSelect = async (file) => {
    setDegreeFile(file); setDegreeUploading(true);
    try {
      const p = await uploadPresigned(file, "degree");
      setDegreePath(p);
    } catch (e) {
      message.error(`Degree upload failed: ${e.message}`);
      setDegreeFile(null); setDegreePath(null);
    } finally { setDegreeUploading(false); }
  };

  const handleReExtractOCR = async (docType) => {
    const talentId = initialData.id;
    if (!talentId) return;
    setExtracting(docType);
    try {
      const token = getToken();
      const res = await axios.post(API_CONST.EXTRACT_TALENT_DOCUMENT, { talentId, documentType: docType }, { headers: { Authorization: `Bearer ${token}` } });
      const { extracted } = res.data;
      if (extracted?.idNumber) {
        if (docType === "aadhaar") form.setFieldValue("aadhaarNumber", extracted.idNumber);
        else if (docType === "pan") form.setFieldValue("panNumber", extracted.idNumber);
        else if (docType === "passport") form.setFieldValue("passportNumber", extracted.idNumber);
        message.success(`ID extracted from ${docType}`);
      } else {
        message.info(`No ID number found in ${docType} document`);
      }
    } catch (e) {
      message.error(e.response?.data?.message || `Failed to extract ${docType}`);
    } finally { setExtracting(null); }
  };

  const anyUploading = resumeUploading || idProofUploading || panUploading || degreeUploading;
  const isEditMode = Boolean(initialData.id);

  return (
    <Form form={form} layout="vertical" initialValues={initialData}>
      <p className="section-title">Availability</p>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Availability" name="availability" rules={[{ required: true, message: "Required" }]}>
            <Select placeholder="Select availability">
              <Option value="Immediately Available">Immediately Available</Option>
              <Option value="Available in 2 weeks">Available in 2 weeks</Option>
              <Option value="Available in 1 month">Available in 1 month</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Notice Period" name="notice" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. 30 days" />
          </Form.Item>
        </Col>
      </Row>

      <p className="section-title" style={{ marginTop: 4 }}>Documents</p>
      <div style={{ padding: "14px 16px", background: "#fffbeb", borderRadius: 8, border: "1px solid #fde68a", marginBottom: 16, fontSize: 12, color: "#92400e" }}>
        <strong>Required:</strong> Resume, ID proof (Aadhaar <em>or</em> Passport), and PAN card. Degree proof is optional. ID numbers are auto-extracted via AI OCR on upload.
      </div>

      <Row gutter={16}>
        <Col span={12}>
          <UploadZone
            label="Resume"
            required
            accept=".pdf,.doc,.docx,.txt"
            hint="PDF, DOC, DOCX, or TXT"
            file={resumeFile}
            path={resumePath}
            uploading={resumeUploading}
            onSelect={handleResumeSelect}
            onRemove={() => { setResumeFile(null); setResumePath(null); }}
            minHeight={98}
          />
        </Col>
        <Col span={12}>
          <div style={{ marginBottom: 6 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#334155", marginBottom: 6 }}>
              ID Proof <span style={{ color: "#ff4d4f" }}>*</span>
            </div>
            <Radio.Group value={idProofType} onChange={(e) => { setIdProofType(e.target.value); setIdProofFile(null); setIdProofPath(null); }} disabled={anyUploading} style={{ marginBottom: 8 }}>
              <Radio value="aadhaar">Aadhaar Card</Radio>
              <Radio value="passport">Passport</Radio>
            </Radio.Group>
          </div>
          <UploadZone
            label={idProofType === "aadhaar" ? "Aadhaar Card" : "Passport"}
            required
            accept=".pdf,.jpg,.jpeg,.png"
            file={idProofFile}
            path={idProofPath}
            uploading={idProofUploading}
            onSelect={handleIdProofSelect}
            onRemove={() => { setIdProofFile(null); setIdProofPath(null); }}
          />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <UploadZone
            label="PAN Card"
            required
            accept=".pdf,.jpg,.jpeg,.png"
            file={panFile}
            path={panPath}
            uploading={panUploading}
            onSelect={handlePanSelect}
            onRemove={() => { setPanFile(null); setPanPath(null); }}
          />
        </Col>
        <Col span={12}>
          <UploadZone
            label="Degree Proof"
            accept=".pdf,.jpg,.jpeg,.png"
            inlineHint="Optional"
            file={degreeFile}
            path={degreePath}
            uploading={degreeUploading}
            onSelect={handleDegreeSelect}
            onRemove={() => { setDegreeFile(null); setDegreePath(null); }}
          />
        </Col>
      </Row>

      {isEditMode && initialData.id && (
        <div style={{ marginTop: 8, padding: "12px 16px", background: "#f0f5ff", borderRadius: 8, border: "1px solid #d6e4ff" }}>
          <div style={{ fontWeight: 600, color: "#1677ff", fontSize: 13, marginBottom: 8 }}>AI OCR — Re-extract ID Numbers from Saved Documents</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { docType: idProofType === "aadhaar" ? "aadhaar" : "passport", label: idProofType === "aadhaar" ? "Extract Aadhaar" : "Extract Passport", hasDoc: !!(idProofPath) },
              { docType: "pan", label: "Extract PAN", hasDoc: !!(panPath) },
            ].map(({ docType, label, hasDoc }) => (
              <Tooltip key={docType} title={hasDoc ? `Re-extract from saved ${docType}` : `No ${docType} uploaded yet`}>
                <button
                  type="button"
                  disabled={!hasDoc || !!extracting}
                  onClick={() => handleReExtractOCR(docType)}
                  style={{
                    height: 30, padding: "0 14px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: hasDoc && !extracting ? "pointer" : "not-allowed",
                    border: `1px solid ${hasDoc ? "#1677ff" : "#e2e8f0"}`, background: "white", color: hasDoc ? "#1677ff" : "#94a3b8", display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  {extracting === docType ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : <RobotOutlined />}
                  {label}
                </button>
              </Tooltip>
            ))}
          </div>
        </div>
      )}

      <p className="section-title" style={{ marginTop: 16 }}>ID Numbers (auto-filled by OCR)</p>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="aadhaarNumber" label="Aadhaar Number">
            <Input placeholder="e.g. 1234 5678 9012" maxLength={20} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="panNumber" label="PAN Number">
            <Input placeholder="e.g. ABCDE1234F" maxLength={20} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="passportNumber" label="Passport Number">
            <Input placeholder="e.g. A1234567" maxLength={40} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default AvailabilityDocuments;
